import { CONFIG } from '../config.js';
import { safeFetchClient } from '../utils/safe-fetch-client.js';

/**
 * Adaptador para la API General Purpose (GP) de Fudo.
 * La GP usa JSON:API: las respuestas vienen en { data: [...] } con
 * { type, id (string), attributes: {...}, relationships: {...} }.
 * Mapea eso a una forma plana que el dominio espera.
 */
export const FudoGpRepository = {

    async get(endpoint, { headers = {} } = {}) {
        try {
            const response = await safeFetchClient(`${CONFIG.API.FUDO_GP}${endpoint}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json', ...headers },
                timeoutMs: CONFIG.API.TIMEOUT_MS,
            });

            if (!response.ok) {
                throw new Error(`Fudo GP Repository Error (${endpoint}): ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            window.Sentry?.captureException(error);
            throw error;
        }
    },

    async getCategories() {
        const res = await this.get('/product-categories?page[size]=200');
        const list = res.data ?? [];
        return list
            // La API GP devuelve type 'ProductCategory' (mayúscula). Comparamos
            // case-insensitive para no vaciar el catálogo si cambia el casing.
            .filter(item => String(item.type).toLowerCase() === 'productcategory')
            .map(item => ({
                id: item.id,
                name: item.attributes?.name ?? '',
                position: item.attributes?.position ?? 0,
            }));
    },

    async getProducts() {
        return (await this.getCatalog()).products;
    },

    /**
     * Catálogo + los GRUPOS de modificadores que los productos referencian.
     *
     * Los grupos NO tienen endpoint de colección propio (404): la única forma de
     * leer su `name`/`priceAggregation` es sideloadearlos con
     * `include=productModifiersGroups`, que cuesta ~3.6% más de payload (34
     * grupos) sobre un request que la carta ya hacía igual.
     *
     * @returns {Promise<{products: Array<object>, modifierGroups: Array<object>}>}
     */
    async getCatalog() {
        // Paginación JSON:API: la GP API tope-a en `page[size]` y NO devuelve
        // links/meta de próxima página, así que iteramos `page[number]` (1-based)
        // hasta que una página venga incompleta (menos de PAGE_SIZE registros).
        // Sin esto un catálogo mayor a PAGE_SIZE se truncaba en silencio (eran
        // 601 productos con un tope de 500). MAX_PAGES es un backstop anti-loop.
        const PAGE_SIZE = 500;
        const MAX_PAGES = 20;
        const raw = [];
        const groupsById = new Map();
        for (let page = 1; page <= MAX_PAGES; page++) {
            const res = await this.get(
                `/products?include=productModifiersGroups&page[size]=${PAGE_SIZE}&page[number]=${page}`
            );
            const chunk = res.data ?? [];
            raw.push(...chunk);
            for (const item of res.included ?? []) {
                if (String(item.type).toLowerCase() !== 'productmodifiersgroup') continue;
                groupsById.set(String(item.id), {
                    id: String(item.id),
                    name: item.attributes?.name ?? '',
                    priceAggregation: item.attributes?.priceAggregation ?? null,
                    minQuantity: item.attributes?.minQuantity ?? null,
                    maxQuantity: item.attributes?.maxQuantity ?? null,
                });
            }
            if (chunk.length < PAGE_SIZE) break;
        }

        const products = raw
            // La API GP devuelve type 'Product' (mayúscula). Comparamos case-insensitive
            // para no vaciar el catálogo si cambia el casing.
            .filter(item => String(item.type).toLowerCase() === 'product')
            .map(item => {
                const attrs = item.attributes ?? {};
                const rels = item.relationships ?? {};
                const categoryRel = rels.productCategory ?? rels.category ?? {};
                const categoryId = categoryRel.data?.id ?? null;

                return {
                    id: item.id,
                    name: attrs.name ?? '',
                    price: attrs.price ?? 0,
                    description: attrs.description ?? null,
                    categoryId,
                    // Orden que el staff define en Fudo. Es la clave para listar
                    // la carta siguiendo el mismo orden del sistema (lo consume
                    // menu.js al ordenar cada categoría). La API de integraciones
                    // NO exponía este campo — por eso se migró la carta a GP.
                    position: attrs.position ?? 0,
                    enableOnlineMenu: attrs.enableOnlineMenu ?? null,
                    ignoreAvailability: attrs.ignoreAvailability ?? false,
                    stock: attrs.stock ?? null,
                    stockControl: attrs.stockControl ?? false,
                    favourite: attrs.favourite ?? false,
                    imageUrl: attrs.imageUrl ?? null,
                    active: attrs.active ?? false,
                    // Grupos de modificadores que el producto ofrece (ej. el grupo
                    // "leche comun o vegetal" en los cafés). La GP API devuelve el
                    // linkage en `relationships` SIN necesidad de `include=`, así que
                    // esto no agrega ni un request al catálogo.
                    modifierGroupIds: (rels.productModifiersGroups?.data ?? [])
                        .map(g => String(g.id)),
                };
            })
            .filter(p => p.active);

        return { products, modifierGroups: [...groupsById.values()] };
    },

    /**
     * Opciones de modificador (`ProductModifier`): el precio de cada opción y a
     * qué grupo y producto pertenece. El NOMBRE de la opción no viene acá — en
     * Fudo cada opción apunta a un producto (`productId`) y de ahí sale el nombre.
     */
    async getModifierOptions() {
        const PAGE_SIZE = 500;
        const MAX_PAGES = 20;
        const raw = [];
        for (let page = 1; page <= MAX_PAGES; page++) {
            const res = await this.get(`/product-modifiers?page[size]=${PAGE_SIZE}&page[number]=${page}`);
            const chunk = res.data ?? [];
            raw.push(...chunk);
            if (chunk.length < PAGE_SIZE) break;
        }
        return raw
            .filter(item => String(item.type).toLowerCase() === 'productmodifier')
            .map(item => ({
                id: item.id,
                price: item.attributes?.price ?? 0,
                maxQuantity: item.attributes?.maxQuantity ?? null,
                productId: item.relationships?.product?.data?.id
                    ? String(item.relationships.product.data.id)
                    : null,
                groupId: item.relationships?.productModifiersGroup?.data?.id
                    ? String(item.relationships.productModifiersGroup.data.id)
                    : null,
            }));
    }
};
